import { useCallback, useEffect, useMemo, type FC } from "react";
import {
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { ToggleSwitch } from "../../../shared";

import {
  buildAutoStartSchedule,
  type AutoStartAnchor,
  type ScheduleValues,
} from "../createSeasonData";

import styles from "./Schedule.module.css";

/** Visual contract for `Schedule`.
 *
 *   wizard   — the Create Season Step 3 chrome (renders the
 *              "Schedule Season" intro header + descriptive copy).
 *   embedded — used by `EditSeasonModal`. The modal supplies its own
 *              section header + info icon, so the component skips the
 *              intro and renders only the auto-start row + date grid.
 */
export type ScheduleVariant = "wizard" | "embedded";

export type ScheduleProps = {
  values: ScheduleValues;
  /** Receives the full snapshot after each field change so the parent can validate. */
  onValuesChange: (values: ScheduleValues) => void;
  /** Defaults to `"wizard"`. See `ScheduleVariant`. */
  variant?: ScheduleVariant;
  /** `data-testid` prefix for the rendered controls. Defaults to
   *  `create-season` (the wizard's namespace); embedded consumers
   *  override this to scope tests to their own surface. */
  testIdPrefix?: string;
  /** When false, renders only the required date/time grid. Useful for
   *  Step 1 where start/end are mandatory before the draft can be created. */
  showAutoStartControls?: boolean;
  /** Renders every control read-only. Used by `EditSeasonModal`'s view mode
   *  (the eye icon on a locked / active season) so the schedule can be
   *  reviewed but never changed. Composes with the existing per-control
   *  disabling (e.g. date pickers already disable while auto-start is on). */
  disabled?: boolean;
  /** The active season the new one auto-starts after (see
   *  `resolveAutoStartAnchor`). When auto-start is toggled on, the start
   *  date+time are auto-filled to one minute after this season's end (and
   *  locked), the end is pre-filled three days later (but stays editable), and
   *  the anchor's id is captured on `previousSeasonId` so the save can link it
   *  via `trigger_season_id`. `null` / omitted leaves the grid empty until the
   *  seasons list resolves (e.g. the embedded Edit Season modal). */
  autoStartAnchor?: AutoStartAnchor | null;
};

const DEFAULT_TEST_ID_PREFIX = "create-season";

const Schedule: FC<ScheduleProps> = ({
  values,
  onValuesChange,
  variant = "wizard",
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  showAutoStartControls = true,
  disabled = false,
  autoStartAnchor = null,
}) => {
  const update = useCallback(
    (patch: Partial<ScheduleValues>) => {
      onValuesChange({ ...values, ...patch });
    },
    [onValuesChange, values],
  );

  // Start (and the default end) the auto-started season inherits from the
  // active season's end instant. Memoised so the toggle handler and the
  // late-load effect below derive the same window.
  const autoStartFill = useMemo(
    () => buildAutoStartSchedule(autoStartAnchor?.end ?? null),
    [autoStartAnchor],
  );

  const handleAutoStartToggle = useCallback(
    (next: boolean) => {
      if (next) {
        // Auto-start chains the new season after the active one. With no active
        // season there is nothing to chain after, so reject the toggle (it stays
        // off) and tell the admin why instead of silently enabling an
        // unschedulable state.
        if (!autoStartAnchor || !autoStartFill.startDate) {
          message.error(AppStrings.AUTO_START_NO_ACTIVE_SEASON);
          return;
        }
        update({
          autoStartAfter: true,
          // Capture the active season's id so the save links it via
          // `trigger_season_id`.
          previousSeasonId: autoStartAnchor.id ?? undefined,
          // Toggling auto-start on is a deliberate "use the computed schedule"
          // action, so overwrite both the locked start (active end + 1 min) and
          // the default end (+3 days) — even over any placeholder window a draft
          // was seeded with. The end stays editable afterwards.
          startDate: autoStartFill.startDate,
          startTime: autoStartFill.startTime,
          endDate: autoStartFill.endDate,
          endTime: autoStartFill.endTime,
        });
        return;
      }
      update({ autoStartAfter: false, previousSeasonId: undefined });
    },
    [autoStartAnchor, autoStartFill, update],
  );

  const autoStartActive = !!values.autoStartAfter;

  // Backfill the locked start, trigger link, and default end when the anchor
  // resolves *after* auto-start was switched on — e.g. the admin toggled it
  // before the seasons list finished loading. Guarded on an empty `startDate`
  // so it fires once and never overwrites an end the admin has since edited.
  useEffect(() => {
    if (!autoStartActive || !autoStartFill.startDate || values.startDate) {
      return;
    }
    update({
      previousSeasonId: autoStartAnchor?.id ?? undefined,
      startDate: autoStartFill.startDate,
      startTime: autoStartFill.startTime,
      endDate: values.endDate ?? autoStartFill.endDate,
      endTime: values.endTime ?? autoStartFill.endTime,
    });
  }, [
    autoStartActive,
    autoStartAnchor,
    autoStartFill,
    update,
    values.startDate,
    values.endDate,
    values.endTime,
  ]);

  const isEmbedded = variant === "embedded";
  // The wizard renders an intro header; the embedded modal owns its own
  // section heading so it skips the intro. `aria-labelledby` is only
  // useful when the local header is actually rendered.
  const containerProps = isEmbedded
    ? {
        "data-testid": `${testIdPrefix}-schedule-embedded`,
        "data-variant": "embedded" as const,
      }
    : {
        "data-testid": `${testIdPrefix}-schedule`,
        "data-variant": "wizard" as const,
        "aria-labelledby": `${testIdPrefix}-schedule-title`,
      };

  return (
    <section className={styles.container} {...containerProps}>
      {!isEmbedded && (
        <header className={styles.intro}>
          <h2
            id={`${testIdPrefix}-schedule-title`}
            className={styles.title}
          >
            {AppStrings.SCHEDULE_SEASON}
          </h2>
          <p className={styles.description}>
            {AppStrings.SCHEDULE_SEASON_DESCRIPTION}
          </p>
        </header>
      )}

      {showAutoStartControls && (
        <div className={styles.autoStartRow}>
          <label
            className={styles.toggleLabel}
            data-testid={`${testIdPrefix}-auto-start-toggle`}
          >
            <ToggleSwitch
              value={autoStartActive}
              onChange={handleAutoStartToggle}
              tone="info"
              disabled={disabled}
            />
            <span>{AppStrings.AUTO_START_AFTER}</span>
          </label>
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.START_DATE}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntDatePicker
            value={values.startDate ?? null}
            onChange={(next: Dayjs | null) =>
              update({ startDate: next ?? undefined })
            }
            disabled={disabled || autoStartActive}
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            className={`${styles.input}${
              autoStartActive ? ` ${styles.lockedInput}` : ""
            }`}
            data-testid={`${testIdPrefix}-start-date`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.START_TIME}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntTimePicker
            value={values.startTime ?? null}
            onChange={(next: Dayjs | null) =>
              update({ startTime: next ?? undefined })
            }
            disabled={disabled || autoStartActive}
            placeholder="12:00 am"
            format="hh:mm a"
            use12Hours
            className={`${styles.input}${
              autoStartActive ? ` ${styles.lockedInput}` : ""
            }`}
            data-testid={`${testIdPrefix}-start-time`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.END_DATE}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntDatePicker
            value={values.endDate ?? null}
            onChange={(next: Dayjs | null) =>
              update({ endDate: next ?? undefined })
            }
            disabled={disabled}
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            className={styles.input}
            data-testid={`${testIdPrefix}-end-date`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.END_TIME}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntTimePicker
            value={values.endTime ?? null}
            onChange={(next: Dayjs | null) =>
              update({ endTime: next ?? undefined })
            }
            disabled={disabled}
            placeholder="11:59 pm"
            format="hh:mm a"
            use12Hours
            className={styles.input}
            data-testid={`${testIdPrefix}-end-time`}
          />
        </div>
      </div>
    </section>
  );
};

export default Schedule;
